import { useState, useEffect, useRef, useCallback } from "react";

interface UseTimedExerciseTimerProps {
  duration: number;
  soundEnabled?: boolean;
  playSound?: (type: "timer" | "rest" | "countdown" | "complete") => void;
  onComplete: () => void;
  isPaused?: boolean;
}

export function useTimedExerciseTimer({
  duration,
  soundEnabled = false,
  playSound,
  onComplete,
  isPaused = false,
}: UseTimedExerciseTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasPlayedCompletionSound = useRef(false);

  // Reset timer when duration changes
  useEffect(() => {
    setTimeRemaining(duration);
    hasPlayedCompletionSound.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [duration]);

  // Timer effect
  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    if (timeRemaining <= 0) {
      if (soundEnabled && !hasPlayedCompletionSound.current) {
        try {
          playSound && playSound("complete");
          hasPlayedCompletionSound.current = true;
        } catch {}
      }
      setTimeout(() => {
        onComplete();
      }, 100);
      return;
    }
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newValue = prev - 1;
        if (newValue <= 3 && newValue > 0 && soundEnabled) {
          try {
            playSound && playSound("countdown");
          } catch {}
        }
        return newValue;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeRemaining, soundEnabled, playSound, onComplete, isPaused]);

  // Manual reset
  const reset = useCallback(() => {
    setTimeRemaining(duration);
    hasPlayedCompletionSound.current = false;
  }, [duration]);

  return {
    timeRemaining,
    setTimeRemaining,
    reset,
    isComplete: timeRemaining <= 0,
  };
}
